/**
 * 执行人表格组件
 * 对应原 Vue 项目 EditPhone.vue
 *
 * 核心设计 — latest ref 模式解决闭包陷阱：
 *   columns 只在 IqueryColumn 返回时设一次（存 state），
 *   但 columns.render 里闭包引用的 handleCellChange/handleSave/... 会随着 dataList 变化而重建，
 *   闭包里的 dataList 永远是第一次渲染的空数组 → 每次输入都会把 dataList 改回 [] → 表格空
 *
 *   修法：所有 handler 存 handlersRef.current（useEffect 每次 render 后同步），
 *   columns.render 只调 handlersRef.current.xxx，永远是最新版本。
 *
 * 硬编码 queryId（必须保持一致）：
 *   - 执行人列表：d72e0f58-f874-4692-8f9c-692859a79284
 */

import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Table,
  Button,
  Input,
  Row,
  Col,
  Dropdown,
  Menu,
  Upload,
  message,
  Space,
  Popconfirm,
  Pagination,
  Empty,
} from "antd";
import { PlusOutlined, DeleteOutlined, DownOutlined } from "@ant-design/icons";
import commonService from "@/service/common";
import SmartStorage from "@/utils/storage";

// 执行人列表查询 ID（硬编码，与原项目保持一致）
const QUERY_ID_PHONE_LIST = "d72e0f58-f874-4692-8f9c-692859a79284";

const EditPhoneTable = ({
  qrData,
  isCreatedQrcode,
  record,
  onCreateQRCode,
  onChangeQRCodeData,
  onHandlePhoneModal,
  onShowLogs,
}) => {
  const [columns, setColumns] = useState([]);
  const [dataList, setDataList] = useState([]);
  const [loading, setLoading] = useState(false);
  // 搜索：搜索框输入临时值 + 搜索后实际应用的过滤词
  const [searchKeyword, setSearchKeyword] = useState("");
  const [appliedKeyword, setAppliedKeyword] = useState("");
  // 当前页
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  // 选中的行 keys
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  /**
   * latest ref — 所有 handler 同步到这个 ref，
   * columns.render 只调 ref.current，彻底绕开闭包陷阱
   */
  const handlersRef = useRef({});

  useEffect(() => {
    getIqueryColumns();
    getIqueryData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ===== handlers（每次 render 重建，下面同步到 ref）=====

  /** 添加执行人（前端临时行）— 对齐 Vue 原项目 addRow */
  const handleAdd = () => {
    const newUId = generateUUID();
    const codeParams = qrData?.codeParams;
    const newRow = {
      uId: newUId,
      key: newUId,
      name: "",
      mobile: "",
      canEdit: true,
      isAdd: true,
      // 这些字段 Vue 原项目 addRow 就带上了,后端执行时会用到
      miceId:
        record?.processId ||
        codeParams?.data?.itemData?.processId ||
        codeParams?.data?.eventData?.processId,
      categoryType: codeParams?.itemType || 1,
      itemId:
        record?._SupplierId ||
        codeParams?.data?.itemData?.itemId ||
        codeParams?.data?.orderDetail?.itemId,
      supplierId:
        record?._SupplierId ||
        codeParams?.data?.itemData?.supplierId ||
        codeParams?.data?.orderDetail?.itemId,
    };
    setDataList([newRow, ...dataList]);
  };

  /** 生成 UUID */
  const generateUUID = () => {
    if (window.crypto && window.crypto.randomUUID) {
      return window.crypto.randomUUID();
    }
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };

  /** 编辑单元格 */
  const handleCellChange = (value, row, field) => {
    setDataList(
      dataList.map((item) =>
        item.uId === row.uId ? { ...item, [field]: value } : item,
      ),
    );
  };

  /** 进入编辑态 */
  const handleEdit = (row) => {
    setDataList(
      dataList.map((item) =>
        item.uId === row.uId
          ? { ...item, canEdit: true, originalMobile: item.mobile }
          : item,
      ),
    );
  };

  /** 取消编辑 */
  const handleCancelEdit = (row) => {
    setDataList(
      dataList
        .map((item) => {
          if (item.uId === row.uId) {
            if (item.isAdd) return null;
            return {
              ...item,
              canEdit: false,
              mobile: item.originalMobile || item.mobile,
            };
          }
          return item;
        })
        .filter(Boolean),
    );
  };

  /** 保存执行人（单条）— 完全对齐 Vue 原项目 saveData */
  const handleSave = async (row) => {
    const reg = /^1[3-9]\d{9}$/;
    if (!reg.test(row.mobile)) {
      message.error("请输入正确的手机号");
      return;
    }
    const codeParams = qrData?.codeParams;
    // Vue 原项目:先展开 row(含 uId/name/mobile/verifyUserId/batchId/supplierId 等后端字段),
    // 再用 codeParams 覆盖 miceId/categoryType/itemId/supplierId
    let params = {
      ...row,
      miceId:
        record?.processId ||
        codeParams?.data?.itemData?.processId ||
        codeParams?.data?.eventData?.processId,
      categoryType: codeParams?.itemType || 1,
      itemId:
        record?._SupplierId ||
        codeParams?.data?.itemData?.itemId ||
        codeParams?.data?.orderDetail?.itemId,
      supplierId:
        record?._SupplierId ||
        codeParams?.data?.itemData?.supplierId ||
        codeParams?.data?.orderDetail?.itemId,
    };
    // 去掉前端状态字段(后端不关心,Vue 原项目新增行虽有 isAdd 但后端忽略)
    delete params.key;
    delete params.canEdit;
    delete params.isAdd;
    delete params.originalMobile;
    delete params.action;
    try {
      const res = await commonService.ExecuteChainSingle(params);
      if (res && res.success) {
        message.success("保存成功");
        getIqueryData();
        if (isCreatedQrcode) {
          const phones = dataList
            .filter((item) => item.mobile)
            .map((item) => item.mobile);
          onChangeQRCodeData(phones);
        }
      } else {
        message.error(res?.message || "保存失败");
      }
    } catch (error) {
      console.error("保存执行人失败:", error);
      message.error("保存失败");
    }
  };

  /** 删除执行人（单条） */
  const handleDelete = async (row) => {
    const params = {
      actorName: "meta-mariadb-actor",
      code: "crossevent_payment_user_save",
      methodName: "DelUser",
      meta: {
        uIds: [row.uId],
      },
    };
    try {
      const res = await commonService.InvokeMethod(params);
      if (res && res.success) {
        message.success("删除成功");
        getIqueryData();
        if (isCreatedQrcode) {
          const phones = dataList
            .filter((item) => item.mobile && item.uId !== row.uId)
            .map((item) => item.mobile);
          onChangeQRCodeData(phones);
        }
      } else {
        message.error(res?.message || "删除失败");
      }
    } catch (error) {
      console.error("删除执行人失败:", error);
      message.error("删除失败");
    }
  };

  /** 批量删除 */
  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning("请选择要删除的执行人");
      return;
    }
    const params = {
      actorName: "meta-mariadb-actor",
      code: "crossevent_payment_user_save",
      methodName: "DelUser",
      meta: { uIds: selectedRowKeys },
    };
    try {
      const res = await commonService.InvokeMethod(params);
      if (res && res.success) {
        message.success("批量删除成功");
        setSelectedRowKeys([]);
        getIqueryData();
        if (isCreatedQrcode) {
          const phones = dataList
            .filter(
              (item) => item.mobile && !selectedRowKeys.includes(item.uId),
            )
            .map((item) => item.mobile);
          onChangeQRCodeData(phones);
        }
      } else {
        message.error(res?.message || "批量删除失败");
      }
    } catch (error) {
      console.error("批量删除失败:", error);
      message.error("批量删除失败");
    }
  };

  /** Excel 批量导入 */
  const handleImport = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await commonService.XSheetExtract(formData);
      if (res && res.success) {
        const codeParams = qrData?.codeParams;
        const importParams = {
          rows: res.content?.rows || res.content || [],
          miceId:
            record?.processId ||
            codeParams?.data?.itemData?.processId ||
            codeParams?.data?.eventData?.processId,
          categoryType: codeParams?.itemType || 1,
          itemId:
            record?._SupplierId ||
            codeParams?.data?.itemData?.itemId ||
            codeParams?.data?.orderDetail?.itemId,
          supplierId:
            record?._SupplierId ||
            codeParams?.data?.itemData?.supplierId ||
            codeParams?.data?.orderDetail?.itemId,
        };
        const importRes = await commonService.ExecuteChainImport(importParams);
        if (importRes && importRes.success) {
          message.success("导入成功");
          getIqueryData();
        } else {
          message.error(importRes?.message || "导入失败");
        }
      } else {
        message.error(res?.message || "Excel 解析失败");
      }
    } catch (error) {
      console.error("导入执行人失败:", error);
      message.error("导入失败");
    }
    return false;
  };

  /** 生成酒店结算二维码 */
  const handleCreateQRCode = () => {
    const phones = dataList
      .filter((item) => item.mobile)
      .map((item) => item.mobile);
    if (phones.length === 0) {
      message.warning("请先添加执行人手机号");
      return;
    }
    onCreateQRCode(phones);
    // 已生成二维码后再次点击生成 = 批量编辑手机号场景
    // 调父组件重新生成后，关闭当前批量编辑弹窗
    if (isCreatedQrcode && onHandlePhoneModal) {
      onHandlePhoneModal();
    }
  };

  // ===== 将所有 handler 同步到 latest ref =====
  useEffect(() => {
    handlersRef.current = {
      handleCellChange,
      handleEdit,
      handleCancelEdit,
      handleSave,
      handleDelete,
      handleAdd,
      handleBatchDelete,
      handleImport,
      handleCreateQRCode,
      onShowLogs,
    };
  });

  // ===== 列定义（render 从 ref 取 handler，绕开闭包）=====

  /**
   * 查询执行人列表列定义 — 只调一次，但 columns.render 里的 handler
   * 必须从 handlersRef.current 取，否则闭包会捕获第一次的空 dataList
   */
  const getIqueryColumns = async () => {
    const params = {
      queryId: QUERY_ID_PHONE_LIST,
      cluster: "default",
      usePaging: false,
    };
    try {
      const res = await commonService.IqueryColumn(params);
      if (res && res.success && res.content) {
        const cols = (res.content.columns || res.content || []).map((item) => ({
          ...item,
          dataIndex: item.name,
          key: item.name,
          align: item.alignContent || "left",
          // render 里通过 handlersRef.current 取最新 handler
          render: (text, row) => {
            if (row.canEdit && item.name !== "action") {
              return (
                <Input
                  value={row[item.name] ?? ""}
                  onChange={(e) =>
                    handlersRef.current.handleCellChange(
                      e.target.value,
                      row,
                      item.name,
                    )
                  }
                  placeholder={"请输入" + (item.title || item.name)}
                />
              );
            }
            return row[item.name] || "-";
          },
        }));
        // 追加操作列 — 同样从 handlersRef.current 取
        cols.push({
          title: "操作",
          dataIndex: "action",
          key: "action",
          align: "center",
          render: (_, row) => {
            const h = handlersRef.current;
            return (
              <Space>
                {row.canEdit ? (
                  <>
                    <Button
                      type="link"
                      size="small"
                      onClick={() => h.handleSave(row)}
                    >
                      保存
                    </Button>
                    <Button
                      type="link"
                      size="small"
                      onClick={() => h.handleCancelEdit(row)}
                    >
                      取消
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      type="link"
                      size="small"
                      onClick={() => h.handleEdit(row)}
                    >
                      编辑
                    </Button>
                    <Popconfirm
                      title="确定删除该执行人?"
                      onConfirm={() => h.handleDelete(row)}
                    >
                      <Button type="link" size="small" danger>
                        删除
                      </Button>
                    </Popconfirm>
                  </>
                )}
              </Space>
            );
          },
        });
        setColumns(cols);
      }
    } catch (error) {
      console.error("查询执行人列定义失败:", error);
    }
  };

  /** 查询执行人列表数据 */
  const getIqueryData = async () => {
    setLoading(true);
    const codeParams = qrData?.codeParams;
    const params = {
      queryId: QUERY_ID_PHONE_LIST,
      cluster: "default",
      usePaging: false,
      parameters: {
        _miceId: record?.processId || codeParams?.data?.eventData?.processId,
        _categoryType: codeParams?.itemType || 1,
        _itemId: record?._SupplierId || codeParams?.data?.orderDetail?.itemId,
        _supplierId:
          record?._SupplierId || codeParams?.data?.orderDetail?.itemId,
      },
    };
    try {
      const res = await commonService.IqueryData(params);
      if (res && res.success) {
        const rows = res.content?.rows || res.content || [];
        setDataList(rows.map((row) => ({ ...row, key: row.uId || row._id })));
      }
    } catch (error) {
      console.error("查询执行人数据失败:", error);
    } finally {
      setLoading(false);
    }
  };

  // ===== 数据派生 =====

  const filteredData = useMemo(() => {
    return dataList.filter((item) => {
      if (!appliedKeyword) return true;
      return (
        (item.mobile && item.mobile.indexOf(appliedKeyword) > -1) ||
        (item.name && item.name.indexOf(appliedKeyword) > -1)
      );
    });
  }, [dataList, appliedKeyword]);

  const pagedData = useMemo(
    () =>
      filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [filteredData, currentPage],
  );

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys,
      onChange: setSelectedRowKeys,
    }),
    [selectedRowKeys],
  );

  const tablePagination = useMemo(
    () => ({
      current: currentPage,
      pageSize: pageSize,
      total: filteredData.length,
      showSizeChanger: true,
      showQuickJumper: true,
      pageSizeOptions: ["10", "20", "50", "100"],
      showTotal: function (total) {
        return "共 " + total + " 条";
      },
      onChange: function (page) {
        setCurrentPage(page);
      },
    }),
    [currentPage, filteredData.length],
  );

  const tableLocale = { emptyText: <Empty description="暂无执行人" /> };

  const importMenu = useMemo(
    () => (
      <Menu>
        <Menu.Item key="import">
          <Upload
            accept=".xlsx,.xls"
            showUploadList={false}
            beforeUpload={handleImport}
          >
            批量导入
          </Upload>
        </Menu.Item>
        <Menu.Item key="logs" onClick={onShowLogs}>
          导入日志
        </Menu.Item>
      </Menu>
    ),
    [handleImport, onShowLogs],
  );

  return (
    <div className="settle-edit-phone">
      <Row style={{ marginBottom: 8 }} gutter={16}>
        <Col className="settle-edit-phone-left" span={12}>
          <Input
            size="small"
            placeholder="请输入执行人姓名/手机号"
            style={{ width: 200, marginRight: 5 }}
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
          />
          <Button
            size="small"
            style={{ marginRight: 5 }}
            onClick={() => {
              setAppliedKeyword(searchKeyword.trim());
              setCurrentPage(1);
            }}
          >
            搜索
          </Button>
          <Button
            size="small"
            onClick={() => {
              setSearchKeyword("");
              setAppliedKeyword("");
              setCurrentPage(1);
            }}
          >
            重置
          </Button>
        </Col>
        <Col className="settle-edit-phone-right" span={12}>
          {false && (
            <Dropdown overlay={importMenu} trigger={["click"]}>
              <Button size="small" type="primary" style={{ marginRight: 5 }}>
                批量导入 <DownOutlined />
              </Button>
            </Dropdown>
          )}
          <Button
            size="small"
            type="primary"
            danger
            style={{ marginRight: 5 }}
            icon={<DeleteOutlined />}
            disabled={selectedRowKeys.length === 0}
            onClick={handleBatchDelete}
          >
            批量删除
          </Button>
          <Button
            size="small"
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
          >
            添加手机号
          </Button>
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={pagedData}
        loading={loading}
        rowSelection={rowSelection}
        pagination={tablePagination}
        rowKey="uId"
        size="small"
        bordered
        locale={tableLocale}
        scroll={{ y: 300 }}
      />

      <div className="settle-edit-phone-btn-box">
        <Button
          className="btn_createqr"
          type="primary"
          block
          onClick={handleCreateQRCode}
        >
          {"生成" + (qrData?.title || "酒店结算二维码")}
        </Button>
      </div>
    </div>
  );
};

export default EditPhoneTable;
